import { useRef, useEffect, useCallback, useState } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";

// The ArchViz Ops Planning Coordinator is a self-contained single-page tool
// (vanilla JS, all state in the browser). It is served as a static asset from
// /public and embedded here full-bleed. We hand it the backend API base + the
// portal JWT via postMessage so its contract-extraction call can reach our
// authenticated server-side proxy instead of calling Anthropic directly.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const PLANNER_FILE = "/archviz-planner.html";

// The planner is one ~600KB HTML file served with no Cache-Control, so browsers
// cache it heuristically and a PM can keep running the previous build for hours
// after a fix ships — which has already produced "the bug is still there"
// reports against code that was fixed. Ask the server what version of the file
// it currently has (a HEAD that bypasses the cache) and put that in the iframe
// URL: the URL changes only when the file actually changes, so the copy is
// always current without re-downloading it on every visit.
async function fetchPlannerVersion() {
  try {
    const res = await fetch(PLANNER_FILE, { method: "HEAD", cache: "no-store" });
    if (!res.ok) return null;
    const tag = res.headers.get("ETag") || res.headers.get("Last-Modified");
    return tag ? tag.replace(/[^A-Za-z0-9]/g, "") : null;
  } catch {
    return null; // never block the planner from loading over a cache optimisation
  }
}

export default function ArchVizPlanner() {
  const iframeRef = useRef(null);
  // null = not resolved yet. The iframe is held back until we know, so it isn't
  // loaded twice (once stale, once fresh) on every visit.
  const [version, setVersion] = useState(null);
  const [resolved, setResolved] = useState(false);
  const { user } = useAuth();

  const sendInit = useCallback(() => {
    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(
      {
        type: "ARCHVIZ_INIT",
        apiBase: BACKEND_URL,
        token: localStorage.getItem("token") || "",
      },
      window.location.origin
    );
  }, []);

  useEffect(() => {
    let alive = true;
    fetchPlannerVersion().then((v) => {
      if (!alive) return;
      setVersion(v);
      setResolved(true);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!resolved) return;
    // The iframe may finish loading before its message listener is attached,
    // so re-send a few times right after mount, then stop.
    const interval = setInterval(sendInit, 1000);
    const stop = setTimeout(() => clearInterval(interval), 6000);
    return () => {
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [sendInit, resolved]);

  return (
    <div className="-m-4 md:-m-6 lg:-m-8 flex flex-col" style={{ height: "calc(100vh - 3.5rem)" }}>
      {["Admin", "COO"].includes(user?.role) && <TimelineMigrationPanel />}
      {/* Negative margins cancel the Layout's content padding so the planner runs
          edge-to-edge; height fills the remaining viewport below the panel above. */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        {resolved && (
          <iframe
            ref={iframeRef}
            title="ArchViz Planner"
            src={`${PLANNER_FILE}?apiBase=${encodeURIComponent(BACKEND_URL)}${
              version ? `&v=${encodeURIComponent(version)}` : ""
            }`}
            onLoad={sendInit}
            style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          />
        )}
      </div>
    </div>
  );
}

// One-time backfill trigger for POST /api/archviz/migrate-timelines — brings
// each already-planned project's sections/phases into the tables the new
// Planning popup (on the project page) reads, so opening it on a project that
// was already scheduled here shows that schedule instead of an empty prompt.
// Additive only: a program that already has any phases (from a prior run of
// this, or because a PM already used the new popup's Parse Agreement) is left
// completely alone — never overwritten, never deleted. Dry-run always runs
// first and writes nothing; Commit only appears after a dry-run's report is on
// screen, so nobody commits blind. See [[archviz-planner-consolidation]].
function TimelineMigrationPanel() {
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [committed, setCommitted] = useState(false);

  async function run(mode) {
    if (mode === "commit") {
      const ok = window.confirm(
        `Write ${report?.migrated?.length || 0} project(s) into the new Planning popup's tables? ` +
        `This only adds data for projects that don't already have any there — nothing is ever overwritten or deleted.`
      );
      if (!ok) return;
    }
    setRunning(true);
    try {
      const res = await apiClient.post("/archviz/migrate-timelines", { mode });
      setReport(res.data);
      if (mode === "commit") {
        setCommitted(true);
        toast.success(`Migrated ${res.data.migrated.length} project(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration check failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="border-b border-border bg-secondary/30 px-4 py-3 md:px-6 shrink-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <AlertTriangle className="w-4 h-4 text-[#B96A43] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-foreground">
              One-time: bring existing planner timelines into the project page's Planning popup
            </p>
            <p className="text-[12px] text-muted-foreground">
              Additive only — a project that already has data in the new tables is always left alone. Dry-run first; it writes nothing.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => run("dry-run")} disabled={running} data-testid="migrate-dry-run">
            {running && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Run Dry-Run
          </Button>
          {report && !committed && (
            <Button size="sm" onClick={() => run("commit")} disabled={running} data-testid="migrate-commit">
              Commit — write {report.migrated.length}
            </Button>
          )}
        </div>
      </div>
      {report && (
        <div className="mt-3 rounded-lg border border-border bg-card p-3 text-[12px] space-y-1.5" data-testid="migrate-report">
          <div><b className="text-foreground">{report.migrated.length}</b> project(s) {committed ? "migrated" : "would be migrated"}: {report.migrated.map(m => `${m.program} (${m.sections}s/${m.phases}p)`).join(", ") || "none"}</div>
          {report.skipped_already_has_data.length > 0 && (
            <div className="text-muted-foreground">Already has data, left alone: {report.skipped_already_has_data.join(", ")}</div>
          )}
          {report.skipped_no_program_link.length > 0 && (
            <div className="text-muted-foreground">No linked program: {report.skipped_no_program_link.join(", ")}</div>
          )}
          {report.skipped_no_sections.length > 0 && (
            <div className="text-muted-foreground">No production sections to bring over: {report.skipped_no_sections.join(", ")}</div>
          )}
          {report.unresolved_artists.length > 0 && (
            <div className="text-[#B96A43]">Couldn't resolve to an HQ resource, left unassigned: {report.unresolved_artists.join(", ")}</div>
          )}
        </div>
      )}
    </div>
  );
}
